import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { adminOrganisedEventsAPI, adminClubsAPI } from '../services/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { playerOrganisedEventSharePath } from '../utils/playerNextPath';

function eventShareUrl(eventId) {
  if (!eventId || typeof window === 'undefined') return '';
  return `${window.location.origin}${playerOrganisedEventSharePath(eventId)}`;
}

async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

const Container = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  max-width: 1100px;
`;

const Title = styled.h2`
  margin: 0 0 0.35rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
`;

const Subtitle = styled.p`
  margin: 0 0 1.5rem 0;
  color: #6b7280;
  font-size: 0.875rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 960px) {
    grid-template-columns: 1.1fr 0.9fr;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  th, td {
    text-align: left;
    padding: 0.65rem 0.5rem;
    border-bottom: 1px solid #e5e7eb;
  }
  th { color: #6b7280; font-weight: 600; }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
`;

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Input = styled.input`
  padding: 0.55rem 0.65rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
`;

const TextArea = styled.textarea`
  padding: 0.55rem 0.65rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  min-height: 100px;
  font-family: inherit;
`;

const Select = styled.select`
  padding: 0.55rem 0.65rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
`;

const Btn = styled.button`
  padding: 0.5rem 0.85rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  border: ${(p) => (p.$primary ? 'none' : '1px solid #cbd5e1')};
  background: ${(p) => (p.$primary ? '#043663' : '#fff')};
  color: ${(p) => (p.$primary ? '#fff' : '#334155')};
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  & + & { margin-left: 0.35rem; }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  background: #e2e8f0;
  color: #334155;
`;

const Msg = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  background: ${(p) => (p.$ok ? '#ecfdf5' : '#fef2f2')};
  color: ${(p) => (p.$ok ? '#065f46' : '#991b1b')};
  font-size: 0.875rem;
`;

const Hint = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
`;

const CoverPreview = styled.img`
  display: block;
  width: 100%;
  height: auto;
  max-height: 360px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-top: 0.35rem;
  background: #0b1220;
`;

const SignupsOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 90;
  background: rgba(4, 54, 99, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
`;

const SignupsModal = styled.div`
  width: 100%;
  max-width: 560px;
  max-height: calc(100vh - 3rem);
  overflow: auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(4, 54, 99, 0.18);
  padding: 1.15rem 1.2rem 1.25rem;
`;

const SignupsHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
`;

const SignupsTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
`;

const SignupsSub = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
  color: #64748b;
`;

const GroupTitle = styled.div`
  margin: 0.7rem 0 0.35rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #043663;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const PlayerRow = styled.div`
  padding: 0.55rem 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.8rem;
  color: #334155;
  &:last-child { border-bottom: none; }
`;

const PlayerName = styled.div`
  font-weight: 700;
  color: #0f172a;
`;

const PlayerMeta = styled.div`
  margin-top: 0.15rem;
  color: #64748b;
  line-height: 1.45;
  word-break: break-word;
`;

const emptyForm = () => ({
  eventType: 'tournament',
  name: '',
  description: '',
  clubId: '',
  priceChf: '0',
  maxParticipants: '16',
  status: 'draft',
  slots: [],
});

function emptySlot() {
  return {
    key: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    id: null,
    label: '',
    startsAt: '',
    endsAt: '',
    maxParticipants: '',
  };
}

function slotsFromEvent(ev) {
  const existing = (ev.slots || []).map((s) => ({
    key: s.id,
    id: s.id,
    label: s.label || '',
    startsAt: toLocalInput(s.startsAt),
    endsAt: toLocalInput(s.endsAt),
    maxParticipants: s.maxParticipants != null ? String(s.maxParticipants) : '',
  }));
  if (existing.length) return existing;
  if (ev?.startsAt) {
    return [
      {
        key: 'from-event-window',
        id: null,
        label: '',
        startsAt: toLocalInput(ev.startsAt),
        endsAt: toLocalInput(ev.endsAt),
        maxParticipants: '',
      },
    ];
  }
  return [];
}

function visibleSignups(bookings) {
  return (bookings || []).filter(
    (b) => b.status === 'confirmed' || b.status === 'pending_payment'
  );
}

function formatSlotHeading(slot, booking) {
  const label = slot?.label || booking?.slotLabel;
  const when = slot?.startsAt || booking?.slotStartsAt;
  const whenText = when ? new Date(when).toLocaleString() : '';
  if (label && whenText) return `${label} · ${whenText}`;
  return label || whenText || 'No group';
}

function groupSignups(event, bookings) {
  const rows = visibleSignups(bookings);
  const slots = Array.isArray(event?.slots) ? event.slots : [];
  if (slots.length <= 1) {
    const only = slots[0];
    return [
      {
        key: only?.id || 'all',
        title: only ? formatSlotHeading(only) : 'Sign-ups',
        rows,
      },
    ];
  }
  const used = new Set();
  const groups = slots.map((slot) => {
    const list = rows.filter((b) => String(b.slotId) === String(slot.id));
    list.forEach((b) => used.add(b.id));
    return {
      key: slot.id,
      title: formatSlotHeading(slot),
      rows: list,
    };
  });
  const leftover = rows.filter((b) => !used.has(b.id));
  if (leftover.length) {
    groups.push({
      key: 'none',
      title: 'No group selected',
      rows: leftover,
    });
  }
  return groups;
}

function coverPublicUrl(eventId, bust) {
  if (!eventId) return null;
  const q = bust ? `?t=${encodeURIComponent(bust)}` : '';
  return `/api/public/organised-events/${eventId}/cover-image${q}`;
}

function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminOrganisedEvents() {
  const { adminUser } = useAdminAuth();
  const canEdit =
    adminUser?.isSuperAdmin ||
    adminUser?.permissions?.organised_events?.edit ||
    adminUser?.permissions?.organised_events?.create ||
    adminUser?.permissions?.settings_tournaments?.edit ||
    adminUser?.permissions?.settings_general?.edit;
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [signupsEventId, setSignupsEventId] = useState(null);
  const [message, setMessage] = useState(null);
  const [clubFilter, setClubFilter] = useState('');
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreviewLocal, setCoverPreviewLocal] = useState(null);
  const [coverBust, setCoverBust] = useState('');
  const [editingHasCover, setEditingHasCover] = useState(false);
  const [editingCoverUrl, setEditingCoverUrl] = useState('');

  const { data, isLoading } = useQuery(['admin-organised-events'], () => adminOrganisedEventsAPI.list(), {
    staleTime: 15 * 1000,
  });
  const events = data?.data?.events || data?.events || [];

  const { data: eventDetail, isLoading: signupsLoading } = useQuery(
    ['admin-organised-event', signupsEventId],
    () => adminOrganisedEventsAPI.get(signupsEventId),
    { enabled: !!signupsEventId, staleTime: 10 * 1000 }
  );
  const signupEvent = eventDetail?.data?.event || eventDetail?.event || null;
  const signupBookings = useMemo(
    () => eventDetail?.data?.bookings || eventDetail?.bookings || [],
    [eventDetail]
  );
  const signupGroups = useMemo(
    () => (signupsEventId ? groupSignups(signupEvent, signupBookings) : []),
    [signupsEventId, signupEvent, signupBookings]
  );
  const signupCount = visibleSignups(signupBookings).length;
  const signupsListEvent = events.find((ev) => ev.id === signupsEventId) || signupEvent;

  // Same catalog as Settings → Clubs (adminClubsAPI). Interceptor + .then(res => res.data)
  // often yields the clubs array directly — match AdminSettingsClubs unwrap.
  const {
    data: clubsData,
    isLoading: clubsLoading,
    isError: clubsError,
    error: clubsErr,
  } = useQuery(['admin-clubs-for-events'], () => adminClubsAPI.list({ includeInactive: false }), {
    staleTime: 60 * 1000,
  });
  const clubs = useMemo(() => {
    const rows = Array.isArray(clubsData)
      ? clubsData
      : Array.isArray(clubsData?.data)
        ? clubsData.data
        : Array.isArray(clubsData?.clubs)
          ? clubsData.clubs
          : [];
    return rows;
  }, [clubsData]);

  const filteredClubs = useMemo(() => {
    const q = String(clubFilter || '')
      .trim()
      .toLowerCase();
    if (!q) return clubs;
    return clubs.filter((c) => {
      const name = String(c.name || '').toLowerCase();
      const canton = String(c.canton || '').toLowerCase();
      return name.includes(q) || canton.includes(q);
    });
  }, [clubs, clubFilter]);

  useEffect(() => {
    if (!coverFile) {
      setCoverPreviewLocal(null);
      return undefined;
    }
    const url = URL.createObjectURL(coverFile);
    setCoverPreviewLocal(url);
    return () => URL.revokeObjectURL(url);
  }, [coverFile]);

  const clearCoverPick = () => {
    setCoverFile(null);
    setCoverPreviewLocal(null);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setClubFilter('');
    clearCoverPick();
    setEditingHasCover(false);
    setEditingCoverUrl('');
    setCoverBust('');
  };

  const invalidate = () => {
    queryClient.invalidateQueries(['admin-organised-events']);
    if (signupsEventId) queryClient.invalidateQueries(['admin-organised-event', signupsEventId]);
  };

  useEffect(() => {
    if (!signupsEventId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setSignupsEventId(null);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [signupsEventId]);

  const saveMutation = useMutation(
    async () => {
      const slots = (form.slots || [])
        .filter((s) => s.startsAt)
        .map((s) => ({
          id: s.id || undefined,
          label: (s.label || '').trim() || null,
          startsAt: new Date(s.startsAt).toISOString(),
          endsAt: s.endsAt ? new Date(s.endsAt).toISOString() : null,
          maxParticipants: s.maxParticipants ? parseInt(s.maxParticipants, 10) : null,
        }));
      if (!slots.length) {
        const err = new Error('Add at least one time slot with a start time.');
        err.response = { data: { message: err.message } };
        throw err;
      }
      const last = slots[slots.length - 1];
      const payload = {
        eventType: form.eventType,
        name: form.name,
        description: form.description,
        clubId: form.clubId || null,
        startsAt: slots[0].startsAt,
        endsAt: last.endsAt || last.startsAt,
        priceChf: Number(form.priceChf),
        maxParticipants: parseInt(form.maxParticipants, 10),
        status: form.status,
        slots,
      };
      let saved;
      if (editingId) {
        saved = await adminOrganisedEventsAPI.update(editingId, payload);
      } else {
        saved = await adminOrganisedEventsAPI.create(payload);
      }
      const eventId =
        editingId ||
        saved?.event?.id ||
        saved?.data?.event?.id ||
        null;
      if (coverFile && eventId) {
        await adminOrganisedEventsAPI.uploadCover(eventId, coverFile);
      }
      return { saved, eventId, uploadedCover: !!(coverFile && eventId) };
    },
    {
      onSuccess: (result) => {
        const base = editingId ? 'Event updated.' : 'Event created.';
        setMessage({
          ok: true,
          text: result.uploadedCover
            ? `${base} Cover uploaded to media bucket.`
            : base,
        });
        resetForm();
        invalidate();
      },
      onError: (e) => setMessage({ ok: false, text: e?.response?.data?.message || 'Save failed' }),
    }
  );

  const coverMutation = useMutation(
    ({ id, file }) => adminOrganisedEventsAPI.uploadCover(id, file),
    {
      onSuccess: (res) => {
        const url =
          res?.data?.coverUrl ||
          res?.coverUrl ||
          res?.data?.event?.coverImageUrl ||
          res?.event?.coverImageUrl ||
          '';
        setMessage({ ok: true, text: 'Cover uploaded to media bucket.' });
        setCoverBust(String(Date.now()));
        setEditingHasCover(true);
        if (url) setEditingCoverUrl(url);
        clearCoverPick();
        invalidate();
      },
      onError: (e) => setMessage({ ok: false, text: e?.response?.data?.message || 'Upload failed' }),
    }
  );

  const actionMutation = useMutation(
    ({ id, action }) => {
      if (action === 'publish') return adminOrganisedEventsAPI.publish(id);
      if (action === 'close') return adminOrganisedEventsAPI.close(id);
      if (action === 'duplicate') return adminOrganisedEventsAPI.duplicate(id);
      if (action === 'cancel') return adminOrganisedEventsAPI.cancel(id);
      return Promise.reject(new Error('Unknown action'));
    },
    {
      onSuccess: (_r, vars) => {
        setMessage({ ok: true, text: `Action “${vars.action}” completed.` });
        invalidate();
      },
      onError: (e) => setMessage({ ok: false, text: e?.response?.data?.message || 'Action failed' }),
    }
  );

  const startEdit = (ev) => {
    setEditingId(ev.id);
    clearCoverPick();
    setEditingHasCover(!!ev.hasCoverImage);
    setEditingCoverUrl(ev.coverImageUrl || '');
    setCoverBust(String(Date.now()));
    setForm({
      eventType: ev.eventType || 'tournament',
      name: ev.name || '',
      description: ev.description || '',
      clubId: ev.clubId || '',
      priceChf: String(ev.priceChf ?? 0),
      maxParticipants: String(ev.maxParticipants ?? 16),
      status: ev.status || 'draft',
      slots: slotsFromEvent(ev),
    });
  };

  return (
    <Container>
      <Title>Events</Title>
      <Subtitle>
        Admin-organised bookable tournaments (and future camps). Separate from league campaigns under Settings →
        Tournaments.
      </Subtitle>

      {message && <Msg $ok={message.ok}>{message.text}</Msg>}

      <Grid>
        <div>
          <h3 style={{ marginTop: 0 }}>All events</h3>
          {isLoading ? (
            <p>Loading…</p>
          ) : events.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No events yet. Create one on the right.</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>When</th>
                  <th>Spots</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => (
                  <tr key={ev.id}>
                    <td>
                      <strong>{ev.name}</strong>
                      <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                        {ev.eventType} · CHF {Number(ev.priceChf).toFixed(2)}
                      </div>
                    </td>
                    <td>
                      {Array.isArray(ev.slots) && ev.slots.length > 1
                        ? `${ev.slots.length} start times`
                        : ev.slots?.[0]?.startsAt
                          ? new Date(ev.slots[0].startsAt).toLocaleString()
                          : ev.startsAt
                            ? new Date(ev.startsAt).toLocaleString()
                            : '—'}
                    </td>
                    <td>
                      {ev.remainingSpots}/{ev.maxParticipants}
                    </td>
                    <td>
                      <Badge>{ev.status}</Badge>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <Btn
                        type="button"
                        onClick={async () => {
                          try {
                            await copyText(eventShareUrl(ev.id));
                            setMessage({ ok: true, text: 'Player link copied.' });
                          } catch {
                            setMessage({ ok: false, text: eventShareUrl(ev.id) });
                          }
                        }}
                      >
                        Copy link
                      </Btn>
                      <Btn type="button" onClick={() => setSignupsEventId(ev.id)}>
                        View sign-ups
                        {Number.isFinite(Number(ev.confirmedCount))
                          ? ` (${ev.confirmedCount})`
                          : ''}
                      </Btn>
                      <Btn type="button" onClick={() => startEdit(ev)}>
                        Edit
                      </Btn>
                      {canEdit && ev.status === 'draft' && (
                        <Btn
                          type="button"
                          $primary
                          onClick={() => actionMutation.mutate({ id: ev.id, action: 'publish' })}
                        >
                          Publish
                        </Btn>
                      )}
                      {canEdit && (ev.status === 'published' || ev.status === 'full') && (
                        <Btn type="button" onClick={() => actionMutation.mutate({ id: ev.id, action: 'close' })}>
                          Close
                        </Btn>
                      )}
                      {canEdit && (
                        <Btn type="button" onClick={() => actionMutation.mutate({ id: ev.id, action: 'duplicate' })}>
                          Duplicate
                        </Btn>
                      )}
                      {canEdit && ev.status !== 'cancelled' && (
                        <Btn
                          type="button"
                          onClick={() => {
                            if (
                              !window.confirm(
                                'Cancel this event and refund all confirmed bookings? This cannot be undone.'
                              )
                            ) {
                              return;
                            }
                            actionMutation.mutate({ id: ev.id, action: 'cancel' });
                          }}
                        >
                          Cancel
                        </Btn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        <div>
          <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit event' : 'Create event'}</h3>
          {editingId ? (
            <Hint style={{ display: 'block', marginBottom: '0.65rem' }}>
              Player link:{' '}
              <span style={{ fontWeight: 600, wordBreak: 'break-all' }}>{eventShareUrl(editingId)}</span>
              {' '}
              <Btn
                type="button"
                onClick={async () => {
                  try {
                    await copyText(eventShareUrl(editingId));
                    setMessage({ ok: true, text: 'Player link copied.' });
                  } catch {
                    setMessage({ ok: false, text: eventShareUrl(editingId) });
                  }
                }}
              >
                Copy link
              </Btn>
            </Hint>
          ) : null}
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              if (!canEdit) return;
              if (!(form.slots || []).some((s) => s.startsAt)) {
                setMessage({ ok: false, text: 'Add at least one time slot with a start time.' });
                return;
              }
              saveMutation.mutate();
            }}
          >
            <Label>
              Event type
              <Select
                value={form.eventType}
                onChange={(e) => setForm((f) => ({ ...f, eventType: e.target.value }))}
              >
                <option value="tournament">Tournament</option>
                <option value="camp">Camp (future)</option>
              </Select>
            </Label>
            <Label>
              Name
              <Input
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="HPS x TPC Bienne"
              />
            </Label>
            <Label>
              Description
              <TextArea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Format, inclusions, what to bring…"
              />
            </Label>
            <Label>
              Club / location
              <Input
                type="search"
                value={clubFilter}
                onChange={(e) => setClubFilter(e.target.value)}
                placeholder="Search club name or canton…"
                style={{ marginBottom: '0.4rem' }}
                disabled={!canEdit}
              />
              <Select
                value={form.clubId}
                onChange={(e) => setForm((f) => ({ ...f, clubId: e.target.value }))}
                disabled={!canEdit || clubsLoading}
              >
                <option value="">
                  {clubsLoading
                    ? 'Loading clubs…'
                    : clubsError
                      ? 'Failed to load clubs'
                      : '— Select club —'}
                </option>
                {form.clubId &&
                  !filteredClubs.some((c) => String(c.id) === String(form.clubId)) &&
                  (() => {
                    const selected = clubs.find((c) => String(c.id) === String(form.clubId));
                    if (!selected) return null;
                    return (
                      <option key={selected.id} value={selected.id}>
                        {selected.name}
                        {selected.canton ? ` (${selected.canton})` : ''}
                      </option>
                    );
                  })()}
                {filteredClubs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.canton ? ` (${c.canton})` : ''}
                  </option>
                ))}
              </Select>
              {clubsError ? (
                <span style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.75rem', color: '#b91c1c' }}>
                  Could not load Settings clubs:{' '}
                  {clubsErr?.response?.data?.message || clubsErr?.message || 'Unknown error'}.
                </span>
              ) : (
                <span style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.75rem', color: '#6b7280' }}>
                  Same list as Settings → Clubs
                  {clubs.length
                    ? ` (${filteredClubs.length}${filteredClubs.length !== clubs.length ? ` of ${clubs.length}` : ''})`
                    : clubsLoading
                      ? ''
                      : ' (none yet)'}
                  .
                </span>
              )}
            </Label>
            <div>
              <Label as="div">
                Time slots
                <Hint>
                  Click + Add time slot for when this event starts. Add more slots only if players
                  should choose a group (e.g. Group A 09:00, Group B 11:00).
                </Hint>
              </Label>
              {(form.slots || []).map((slot, index) => (
                <div
                  key={slot.key || slot.id || index}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.45rem',
                    padding: '0.65rem',
                    marginTop: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    background: '#fff',
                  }}
                >
                  <div
                    style={{
                      gridColumn: '1 / -1',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: '#334155',
                    }}
                  >
                    Time slot {index + 1}
                  </div>
                  <Label>
                    Label (optional)
                    <Input
                      value={slot.label}
                      placeholder={`e.g. Group ${String.fromCharCode(65 + index)}`}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          slots: f.slots.map((s, i) =>
                            i === index ? { ...s, label: e.target.value } : s
                          ),
                        }))
                      }
                    />
                  </Label>
                  <Label>
                    Max spots (optional)
                    <Input
                      type="number"
                      min="1"
                      placeholder="Same as event"
                      value={slot.maxParticipants}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          slots: f.slots.map((s, i) =>
                            i === index ? { ...s, maxParticipants: e.target.value } : s
                          ),
                        }))
                      }
                    />
                  </Label>
                  <Label>
                    Starts
                    <Input
                      type="datetime-local"
                      required
                      value={slot.startsAt}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          slots: f.slots.map((s, i) =>
                            i === index ? { ...s, startsAt: e.target.value } : s
                          ),
                        }))
                      }
                    />
                  </Label>
                  <Label>
                    Ends (optional)
                    <Input
                      type="datetime-local"
                      value={slot.endsAt}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          slots: f.slots.map((s, i) =>
                            i === index ? { ...s, endsAt: e.target.value } : s
                          ),
                        }))
                      }
                    />
                  </Label>
                  <Btn
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        slots: f.slots.filter((_, i) => i !== index),
                      }))
                    }
                    style={{ gridColumn: '1 / -1', justifySelf: 'start' }}
                  >
                    Remove
                  </Btn>
                </div>
              ))}
              <Btn
                type="button"
                onClick={() => setForm((f) => ({ ...f, slots: [...f.slots, emptySlot()] }))}
                style={{ marginTop: '0.5rem' }}
              >
                + Add time slot
              </Btn>
            </div>
            <Label>
              Price per participant (CHF)
              <Input
                type="number"
                min="0"
                step="0.01"
                required
                value={form.priceChf}
                onChange={(e) => setForm((f) => ({ ...f, priceChf: e.target.value }))}
              />
            </Label>
            <Label>
              Maximum participants
              <Input
                type="number"
                min="1"
                required
                value={form.maxParticipants}
                onChange={(e) => setForm((f) => ({ ...f, maxParticipants: e.target.value }))}
              />
            </Label>
            <Label>
              Status
              <Select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="full">Full</option>
                <option value="closed">Closed</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </Label>
            <Label>
              Cover image
              <Input
                type="file"
                accept="image/*"
                disabled={!canEdit || saveMutation.isLoading || coverMutation.isLoading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  if (editingId) {
                    coverMutation.mutate({ id: editingId, file });
                    return;
                  }
                  setCoverFile(file);
                }}
                style={{ padding: '0.35rem 0' }}
              />
              <Hint>
                {coverMutation.isLoading || (saveMutation.isLoading && coverFile)
                  ? 'Uploading cover to Cloudflare R2…'
                  : editingId
                    ? 'Upload replaces the current cover in the media bucket (Postgres stores the URL only).'
                    : 'Optional — pick a photo now; it uploads to the media bucket right after the event is created.'}
              </Hint>
              {(coverPreviewLocal ||
                (editingId && editingHasCover && !coverPreviewLocal)) && (
                <CoverPreview
                  src={
                    coverPreviewLocal ||
                    editingCoverUrl ||
                    coverPublicUrl(editingId, coverBust)
                  }
                  alt="Cover preview"
                />
              )}
              {coverFile && !editingId && (
                <Btn type="button" onClick={clearCoverPick} style={{ marginTop: '0.35rem', alignSelf: 'flex-start' }}>
                  Remove selected file
                </Btn>
              )}
            </Label>
            <div>
              <Btn type="submit" $primary disabled={!canEdit || saveMutation.isLoading || coverMutation.isLoading}>
                {editingId ? 'Save changes' : 'Create event'}
              </Btn>
              {editingId && (
                <Btn
                  type="button"
                  onClick={() => {
                    resetForm();
                  }}
                >
                  Cancel edit
                </Btn>
              )}
            </div>
          </Form>
        </div>
      </Grid>

      {signupsEventId &&
        createPortal(
          <SignupsOverlay onClick={() => setSignupsEventId(null)}>
            <SignupsModal
              role="dialog"
              aria-modal="true"
              aria-labelledby="event-signups-title"
              onClick={(e) => e.stopPropagation()}
            >
              <SignupsHead>
                <div>
                  <SignupsTitle id="event-signups-title">
                    Sign-ups{signupsLoading ? '' : ` (${signupCount})`}
                  </SignupsTitle>
                  <SignupsSub>
                    {signupsListEvent?.name || 'Event'}
                    {signupsListEvent?.eventType ? ` · ${signupsListEvent.eventType}` : ''}
                  </SignupsSub>
                </div>
                <Btn type="button" onClick={() => setSignupsEventId(null)}>
                  Close
                </Btn>
              </SignupsHead>
              {signupsLoading ? (
                <Hint>Loading sign-ups…</Hint>
              ) : signupCount === 0 ? (
                <Hint>No players have signed up yet.</Hint>
              ) : (
                signupGroups.map((group) => (
                  <div key={group.key}>
                    {signupGroups.length > 1 || (group.title && group.title !== 'Sign-ups') ? (
                      <GroupTitle>
                        {group.title} · {group.rows.length}
                      </GroupTitle>
                    ) : null}
                    {group.rows.length === 0 ? (
                      <Hint>No sign-ups in this group.</Hint>
                    ) : (
                      group.rows.map((b) => (
                        <PlayerRow key={b.id}>
                          <PlayerName>
                            {b.playerName || 'Unknown player'}
                            {b.status === 'pending_payment' ? (
                              <Badge style={{ marginLeft: '0.4rem' }}>Pending payment</Badge>
                            ) : null}
                          </PlayerName>
                          <PlayerMeta>
                            {b.email || 'No email'}
                            {b.phoneNumber ? ` · ${b.phoneNumber}` : ''}
                            <br />
                            Skill: {b.skillLevel != null ? b.skillLevel : '—'}
                            {b.homeClub ? ` · ${b.homeClub}` : ''}
                            <br />
                            Player ID: {b.playerId || '—'}
                            {b.playerId ? (
                              <>
                                {' '}
                                <Btn
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      await copyText(b.playerId);
                                      setMessage({ ok: true, text: 'Player ID copied.' });
                                    } catch {
                                      setMessage({ ok: false, text: b.playerId });
                                    }
                                  }}
                                >
                                  Copy ID
                                </Btn>
                              </>
                            ) : null}
                          </PlayerMeta>
                        </PlayerRow>
                      ))
                    )}
                  </div>
                ))
              )}
            </SignupsModal>
          </SignupsOverlay>,
          document.body
        )}
    </Container>
  );
}
